import { generateUUIDv4, getOrCreateContactUuid } from '../utils/contactUuid';

const STORAGE_KEY = 'widget_contact_uuid';

class _SessionManager {
	constructor() {
		this._mode = 'anonymous'; // 'anonymous' | 'authenticated'
		this._sessionId = null;
		this._metadata = {};
		this._contactUuid = null;
		this._botId = null;
		this._ready = false;
		this._pendingEvents = [];
		this._subscribers = [];
	}

	/**
	 * Called once at startup with the auto-generated anonymous UUID and botId.
	 */
	initialize(anonymousUuid, botId) {
		this._contactUuid = anonymousUuid;
		this._botId = botId;
	}

	/**
	 * Called after origin verification succeeds.
	 * Flushes any session events that arrived before the widget was ready.
	 */
	setReady() {
		if (this._ready) return;
		this._ready = true;

		const pending = [...this._pendingEvents];
		this._pendingEvents = [];
		pending.forEach(detail => this._processEvent(detail));
	}

	/**
	 * Returns the anonymous UUID (always the same, never changes based on auth).
	 * Used for socket connections and as contactId in API calls.
	 */
	getContactId() {
		return this._contactUuid;
	}

	/**
	 * Returns the sessionId with botId appended if authenticated, null otherwise.
	 * Format: {sessionId}_{botId}
	 * This is passed as an additional parameter to API calls.
	 */
	getSessionId() {
		if (this._mode === 'authenticated' && this._sessionId) {
			return this._botId ? `${this._sessionId}_${this._botId}` : this._sessionId;
		}
		return null;
	}

	/**
	 * Returns the raw effective contact ID (without botId).
	 * Uses sessionId if authenticated, otherwise falls back to anonymous UUID.
	 */
	getRawContactId() {
		return this._sessionId || this._contactUuid || getOrCreateContactUuid()
	}
	/**
	 * Returns the effective socket contact ID with botId appended.
	 * Format: {sessionId || uuid}_{botId}
	 * This is what the socket connects with and what handlers should match on.
	 */
	getSocketContactId() {
		const rawId = this._sessionId || this._contactUuid;
		return this._botId ? `${rawId}_${this._botId}` : rawId;
	}

	/**
	 * Returns the stored botId.
	 */
	getBotId() {
		return this._botId;
	}

	/**
	 * Returns current metadata (empty object if none).
	 */
	getMetadata() {
		return { ...this._metadata };
	}

	/**
	 * Returns true if operating with an authenticated session.
	 */
	isAuthenticated() {
		return this._mode === 'authenticated';
	}

	/**
	 * Handles an incoming session event from the parent.
	 * Queues the event if the manager is not yet ready.
	 */
	handleSessionEvent(detail) {
		if (!detail || !detail.action) return;

		if (!this._ready) {
			this._pendingEvents.push(detail);
			return;
		}

		this._processEvent(detail);
	}

	/**
	 * Subscribe to identity changes. Returns an unsubscribe function.
	 */
	onIdentityChange(callback) {
		this._subscribers.push(callback);
		return () => {
			this._subscribers = this._subscribers.filter(cb => cb !== callback);
		};
	}

	/**
	 * Sends an acknowledgment back to the parent window via postMessage.
	 */
	sendAck(callbackId, action, success) {
		if (!callbackId) return;
		try {
			window.parent.postMessage({
				type: 'intelswift:session:ack',
				detail: {
					callback_id: callbackId,
					success,
					action,
					timestamp: Date.now(),
				}
			}, '*');
		} catch (e) {
			console.error('[SessionManager] Failed to send ack:', e);
		}
	}

	// --- Private ---

	_processEvent(detail) {
		const { action, session_id, metadata, callback_id } = detail;

		switch (action) {
			case 'set':
				this._handleSet(session_id, metadata, callback_id);
				break;
			case 'clear':
				this._handleClear(callback_id);
				break;
			case 'refresh':
				this._handleRefresh(session_id, callback_id);
				break;
			default:
				console.warn('[SessionManager] Unknown action:', action);
				this.sendAck(callback_id, action, false);
		}
	}

	_handleSet(sessionId, metadata, callbackId) {
		if (!sessionId) {
			console.error('[SessionManager] set action requires session_id');
			this.sendAck(callbackId, 'set', false);
			return;
		}

		const previousSessionId = this._sessionId;

		this._mode = 'authenticated';
		this._sessionId = sessionId;
		this._metadata = metadata || {};

		console.info('[Intelswift Messenger] User connected as authenticated user. Session ID:', sessionId, '| User info:', this._metadata);
		this.sendAck(callbackId, 'set', true);

		if (this._sessionId !== previousSessionId) {
			this._notifySubscribers();
		}
	}

	_handleClear(callbackId) {
		const wasAuthenticated = this._mode === 'authenticated';

		// Generate a brand new anonymous UUID
		const newUuid = generateUUIDv4();
		this._contactUuid = newUuid;
		this._mode = 'anonymous';
		this._sessionId = null;
		this._metadata = {};

		// Persist the new UUID
		this._persistUuid(newUuid);

		console.info('[Intelswift Messenger] User logged out. Switched to anonymous visitor.');
		this.sendAck(callbackId, 'clear', true);

		// Always notify on clear since identity changes
		if (wasAuthenticated) {
			this._notifySubscribers();
		}
	}

	_handleRefresh(sessionId, callbackId) {
		if (!sessionId) {
			console.error('[SessionManager] refresh action requires session_id');
			this.sendAck(callbackId, 'refresh', false);
			return;
		}

		const previousSessionId = this._sessionId;

		this._sessionId = sessionId;
		// mode stays 'authenticated', metadata stays unchanged

		console.info('[Intelswift Messenger] Session refreshed. New session ID:', sessionId);
		this.sendAck(callbackId, 'refresh', true);

		if (this._sessionId !== previousSessionId) {
			this._notifySubscribers();
		}
	}

	_notifySubscribers() {
		this._subscribers.forEach(cb => {
			try {
				cb();
			} catch (e) {
				console.error('[SessionManager] Subscriber error:', e);
			}
		});
	}

	_persistUuid(uuid) {
		try {
			localStorage.setItem(STORAGE_KEY, uuid);
		} catch (e) { }
		try {
			document.cookie = `${STORAGE_KEY}=${uuid}; Path=/; Max-Age=31536000; SameSite=None; Secure; Partitioned`;
		} catch (e) { }
	}
}

// Singleton export
const SessionManager = new _SessionManager();
export { SessionManager };
